import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Heading, Text, Input, Select, Button, LoadingSpinner } from '../../components/ui'
import { PermissionGuard } from '../../components/PermissionGuard'
import { modulesService } from '../../services/modules'
import { clientsService } from '../../services/clients'
import { BR_UFS } from '../../utils/brUfs'
import {
  formatIdentificationDisplay,
  normalizeIdentificationDigits,
} from '../../utils/clientIdentification'
import type { Client } from '../../types/clients'

const MODULE_NAME = 'comercial_clientes'
const ITEMS_PER_PAGE = 20

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message
  if (typeof error === 'string') return error
  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message?: unknown }).message
    if (typeof message === 'string' && message.trim()) return message
  }
  return 'Erro inesperado ao carregar clientes.'
}

const ComercialClientes: React.FC = () => {
  const navigate = useNavigate()
  const [pageError, setPageError] = React.useState<string | null>(null)
  const [items, setItems] = React.useState<Client[]>([])
  const [loadingListagem, setLoadingListagem] = React.useState(false)
  const [hasSearched, setHasSearched] = React.useState(false)
  const [currentPage, setCurrentPage] = React.useState(1)

  const [filterName, setFilterName] = React.useState('')
  const [filterCode, setFilterCode] = React.useState('')
  const [filterIdentification, setFilterIdentification] = React.useState('')
  const [filterUf, setFilterUf] = React.useState('')

  const [moduleInfo, setModuleInfo] = React.useState<{
    display_name: string
    description: string | null
  } | null>(null)

  const hasFiltersActive = Boolean(
    filterName.trim() || filterCode.trim() || filterIdentification.trim() || filterUf
  )

  React.useEffect(() => {
    const loadModule = async () => {
      const result = await modulesService.getByName(MODULE_NAME)
      if (result.success && result.data) {
        setModuleInfo({
          display_name: result.data.display_name || 'Clientes',
          description: result.data.description || null,
        })
      }
    }
    void loadModule()
  }, [])

  const applyFilters = async () => {
    const codeRaw = filterCode.trim()
    const codeDigits = codeRaw.replace(/\D/g, '')
    if (codeRaw && !codeDigits) {
      setPageError('Informe um código SCGP numérico.')
      setHasSearched(false)
      setItems([])
      return
    }

    setCurrentPage(1)
    setHasSearched(true)
    setLoadingListagem(true)
    setPageError(null)
    try {
      const data = await clientsService.list({
        name: filterName.trim() || undefined,
        code: codeDigits || undefined,
        identification: filterIdentification.trim() || undefined,
        state: filterUf || undefined,
      })
      setItems(data)
    } catch (error) {
      setPageError(getErrorMessage(error))
      setItems([])
    } finally {
      setLoadingListagem(false)
    }
  }

  const clearFilters = () => {
    setFilterName('')
    setFilterCode('')
    setFilterIdentification('')
    setFilterUf('')
    setCurrentPage(1)
    setHasSearched(false)
    setItems([])
    setPageError(null)
  }

  const totalPages = Math.max(1, Math.ceil(items.length / ITEMS_PER_PAGE))
  const pagedItems = React.useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE
    return items.slice(start, start + ITEMS_PER_PAGE)
  }, [items, currentPage])

  React.useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages)
  }, [currentPage, totalPages])

  return (
    <main className="flex-1 flex flex-col overflow-x-hidden overflow-y-auto bg-gray-100 dark:bg-gray-900">
      <PermissionGuard
        moduleName={MODULE_NAME}
        permissionType="show"
        fallback={
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <Text size="xl" className="text-gray-500 dark:text-gray-400 mb-2">
                Acesso Negado
              </Text>
              <Text size="sm" color="muted">
                Você não tem permissão para acessar esta página.
              </Text>
            </div>
          </div>
        }
      >
        <div className="h-full flex flex-col">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">
              {moduleInfo?.display_name || 'Clientes'}
            </Heading>
            {moduleInfo?.description && (
              <Text size="sm" color="muted" className="mt-1">
                {moduleInfo.description}
              </Text>
            )}
          </div>

          <div className="p-6 space-y-4">
            {pageError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
                <Text size="sm">{pageError}</Text>
              </div>
            )}

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
              <div className="mb-4">
                <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                  Filtros
                </Heading>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="min-w-0">
                  <Input
                    placeholder="Nome do cliente"
                    value={filterName}
                    onChange={(e) => setFilterName(e.target.value)}
                    className="w-full"
                  />
                </div>
                <div className="min-w-0">
                  <Input
                    placeholder="Código SCGP"
                    value={filterCode}
                    onChange={(e) => setFilterCode(e.target.value)}
                    className="w-full"
                  />
                </div>
                <div className="min-w-0">
                  <Input
                    placeholder="CPF / CEI / CNPJ"
                    value={filterIdentification}
                    onChange={(e) =>
                      setFilterIdentification(normalizeIdentificationDigits(e.target.value))
                    }
                    className="w-full"
                  />
                </div>
                <div className="min-w-0">
                  <Select
                    value={filterUf}
                    onChange={(e) => setFilterUf(e.target.value)}
                    className="w-full"
                  >
                    <option value="">Todas UFs</option>
                    {BR_UFS.map((uf) => (
                      <option key={uf} value={uf}>
                        {uf}
                      </option>
                    ))}
                  </Select>
                </div>
              </div>
              <div className="mt-4 flex justify-end">
                <Button
                  variant="solid"
                  size="sm"
                  onClick={() => void applyFilters()}
                  className="mr-2"
                  disabled={loadingListagem}
                >
                  Buscar
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearFilters}
                  disabled={loadingListagem || (!hasSearched && !hasFiltersActive)}
                >
                  Limpar filtros
                </Button>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-x-auto">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-3">
                <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                  Clientes
                  <span className="ml-2 text-base font-normal text-gray-500 dark:text-gray-400">
                    ({hasSearched ? items.length : 0} registros)
                  </span>
                </Heading>
              </div>
              <table className="w-full min-w-[800px] divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Cliente
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Código SCGP
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Identificação
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      UF/Cidade
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {!hasSearched ? (
                    <tr>
                      <td colSpan={4} className="px-4 py-8 text-center">
                        <Text size="sm" color="muted">
                          Clique em Buscar para carregar os registros.
                        </Text>
                      </td>
                    </tr>
                  ) : loadingListagem ? (
                    <tr>
                      <td colSpan={4} className="px-4 py-8 text-center">
                        <div className="flex items-center justify-center">
                          <LoadingSpinner />
                        </div>
                      </td>
                    </tr>
                  ) : items.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-4 py-8 text-center">
                        <Text size="sm" color="muted">
                          Nenhum cliente encontrado com os filtros aplicados.
                        </Text>
                      </td>
                    </tr>
                  ) : (
                    pagedItems.map((item) => (
                      <tr
                        key={item.id}
                        role="link"
                        tabIndex={0}
                        onClick={() => navigate(`/comercial/clientes/${item.id}`)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            navigate(`/comercial/clientes/${item.id}`)
                          }
                        }}
                        className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                      >
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 max-w-[40ch] whitespace-normal break-words">
                          {item.name?.trim() || '—'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                          {item.id_scgp || '—'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                          {item.identification
                            ? formatIdentificationDisplay(item.identification)
                            : '—'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                          {[item.state, item.city].filter(Boolean).join(' / ') || '—'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              {hasSearched && !loadingListagem && totalPages > 1 && (
                <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
                  <Text size="sm" color="muted">
                    Página {currentPage} de {totalPages}
                  </Text>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      Anterior
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                    >
                      Próximo
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </PermissionGuard>
    </main>
  )
}

export default ComercialClientes
